import { Component } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, formValueSelector, reduxForm } from "redux-form";

import { type IDownloadGDAFileParams } from "../../api/WebApiClient.js";
import { type ISelectOption, simpleFieldValidation } from "../../app-utils/form.js";
import { fileNameFieldValidation } from "../../app-utils/validation.js";
import { type ColumnAnnotation, type DatasetColumn } from "../../graphql.js";
import { beginExportChartData } from "../../redux/modules/data-export.js";
import {
  type IDatasetViewerState,
  SAMPLES_ANNOTATION,
  type SelectedDataSeries,
  countNumberOfActiveFilters,
  exportAsGdaFile,
} from "../../redux/modules/dataset-viewer.js";
import { type IAppState } from "../../redux/reducer.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { sanitizeFilename } from "@/app-utils/export.tsx";
import { DATA_VIEWER_MODE, EXPORT_FILE_FORMATS } from "@/settings/settings.js";
import { Addon } from "@/ui-modules/core/redux-form/Addon.js";

interface IExportDialogOwnProps {
  onClose: () => void;
}

interface IExportDialogAppStateProps {
  // data mapped from the redux store
  datasetViewerState: IDatasetViewerState;
  exportOption: any;
  exportingChart: boolean;
  chartExportError: string;
  initialValues: Partial<any>;
}

interface IExportDialogDispatchProps {
  exportImage: (fileName: string) => void;
  exportGdaFile: (payload: IDownloadGDAFileParams) => Promise<any>;
}

export interface IExportDialogProps
  extends IExportDialogAppStateProps,
    IExportDialogDispatchProps,
    IExportDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState, _ownProps: IExportDialogProps): IExportDialogAppStateProps {
  const exportOption: ISelectOption = formValueSelector(AnalyticalDataExportDialog.FORM_NAME)(
    state,
    AnalyticalDataExportDialog.FIELD_EXPORT_OPTION,
  );
  const fileName: string = sanitizeFilename(state.datasetViewer.dataset.name);
  return {
    datasetViewerState: state.datasetViewer,
    exportOption: exportOption?.value,
    exportingChart: state.dataExport.shouldExportChart,
    chartExportError: state.dataExport.exportError,
    initialValues: {
      [AnalyticalDataExportDialog.FIELD_EXPORT_OPTION]: AnalyticalDataExportDialog.FIELD_EXPORT_OPTIONS[1],
      [AnalyticalDataExportDialog.FIELD_EXPORT_SAMPLES]: AnalyticalDataExportDialog.FIELD_EXPORT_SAMPLES_OPTIONS[1],
      [AnalyticalDataExportDialog.FIELD_EXPORT_CONDITIONS]:
        AnalyticalDataExportDialog.FIELD_EXPORT_CONDITIONS_OPTIONS[1],
      [AnalyticalDataExportDialog.FIELD_EXPORT_DATASERIES]:
        AnalyticalDataExportDialog.FIELD_EXPORT_DATASERIES_OPTIONS[1],
      [AnalyticalDataExportDialog.FIELD_EXPORT_FILENAME]: fileName.substring(0, fileName.lastIndexOf(".")) ?? fileName,
    },
  };
}

function mapDispatchToProps(dispatch): IExportDialogDispatchProps {
  return {
    exportImage: (fileName: string) => dispatch(beginExportChartData(fileName)),
    exportGdaFile: (payload: IDownloadGDAFileParams) => dispatch(exportAsGdaFile(payload)),
  };
}

class AnalyticalDataExportDialog extends Component<IExportDialogProps> {
  public static FORM_NAME = "exportAnalyticalData";

  public static FIELD_EXPORT_OPTION = "exportOption";
  public static FIELD_EXPORT_SAMPLES = "exportSamples";
  public static FIELD_EXPORT_CONDITIONS = "exportConditions";
  public static FIELD_EXPORT_DATASERIES = "exportDataseries";
  public static FIELD_EXPORT_FILENAME = "exportFileName";

  public imageExportResolve: () => void;
  public imageExportReject: (error: Error) => void;

  public static FIELD_EXPORT_OPTIONS: ISelectOption[] = [
    { label: "Text file (.gda)", value: EXPORT_FILE_FORMATS.GDA_FORMAT },
    { label: "Image (.png)", value: EXPORT_FILE_FORMATS.PNG_FORMAT },
  ];

  public static FIELD_EXPORT_SAMPLES_OPTIONS: ISelectOption[] = [
    { label: "All samples", value: "ALL" },
    { label: "Filtered samples", value: "FILTERED" },
  ];

  public static FIELD_EXPORT_CONDITIONS_OPTIONS: ISelectOption[] = [
    { label: "All x-axis condition values", value: "ALL" },
    { label: "Selected x-axis condition values", value: "SELECTED" },
    { label: "No x-axis condition values", value: "NONE" },
  ];

  public static FIELD_EXPORT_DATASERIES_OPTIONS: ISelectOption[] = [
    { label: "All series", value: "ALL" },
    { label: "Selected series", value: "SELECTED" },
  ];

  constructor(props: IExportDialogProps) {
    super(props);

    this.handleSubmitData = this.handleSubmitData.bind(this);
    this.getFieldExportOptions = this.getFieldExportOptions.bind(this);
  }

  componentDidUpdate(prevProps: IExportDialogProps) {
    if (prevProps.submitting && prevProps.exportingChart && !this.props.exportingChart) {
      if (this.props.chartExportError) {
        this.imageExportReject(new Error(this.props.chartExportError));
      } else {
        this.imageExportResolve();
      }
    }
  }

  handleSubmitData(values): Promise<any> {
    const viewer: IDatasetViewerState = this.props.datasetViewerState;

    let exportPromise: Promise<void>;
    const inputFileName: string = values[AnalyticalDataExportDialog.FIELD_EXPORT_FILENAME] as string;
    const fileName: string = inputFileName || (viewer.dataset && viewer.dataset.name);

    if (
      values[AnalyticalDataExportDialog.FIELD_EXPORT_OPTION] &&
      values[AnalyticalDataExportDialog.FIELD_EXPORT_OPTION].value === EXPORT_FILE_FORMATS.PNG_FORMAT
    ) {
      exportPromise = new Promise<void>((resolve, reject) => {
        this.imageExportResolve = resolve; // will be resolved or rejected later on
        this.imageExportReject = reject;
        this.props.exportImage(fileName);
      });
    } else {
      const exportSample: ISelectOption = values[AnalyticalDataExportDialog.FIELD_EXPORT_SAMPLES];
      const exportConditions: ISelectOption = values[AnalyticalDataExportDialog.FIELD_EXPORT_CONDITIONS];
      const exportDataseries: ISelectOption = values[AnalyticalDataExportDialog.FIELD_EXPORT_DATASERIES];
      // create the payload of export parameters

      // what columns to export ?
      const activeFilter: boolean = countNumberOfActiveFilters(viewer.filteredOutAnnotations) > 0;
      const exportAllSamples: boolean = exportSample && exportSample.value === "ALL";
      const exportColumns: DatasetColumn[] =
        exportAllSamples || activeFilter === false
          ? viewer.dataset.columns
          : viewer.dataset.columns.filter((column) => !viewer.filteredOutColumns.includes(column.field));
      // what conditions to export ?
      let exportAnnotations: ColumnAnnotation[] = [];
      const fieldExportCondition: string = exportConditions?.value;
      switch (fieldExportCondition) {
        case "ALL":
          exportAnnotations = viewer.availableAnnotations.filter(
            (a: ColumnAnnotation) => a.name !== SAMPLES_ANNOTATION.name,
          );
          break;
        case "SELECTED":
          exportAnnotations = viewer.selectedAnnotations.filter(
            (a: ColumnAnnotation) => a.name !== SAMPLES_ANNOTATION.name,
          );
          break;
        case "NONE":
          exportAnnotations = [];
          break;
      }
      // what data series to export ?
      const exportAllDataseries: boolean = exportDataseries && exportDataseries.value === "ALL";
      const exportDataSeries: SelectedDataSeries[] = exportAllDataseries ? [] : viewer.selectedDataSeries;

      const exportPayload: IDownloadGDAFileParams = {
        id: viewer.dataset.id,
        name: fileName,
        extension: "gda",
        columns: exportColumns.map((c) => c.name),
        annotations: exportAnnotations.map((a) => a.name),
        dataSeries: exportDataSeries.map((d) => d.name),
      };

      exportPromise = this.props.exportGdaFile(exportPayload);
    }

    return exportPromise.then(
      (fulfilled) => {
        this.props.onClose();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  }

  getFieldExportOptions() {
    // do not provide the gda export option if viewing sample data
    if (this.props.datasetViewerState.mode === DATA_VIEWER_MODE.GROUP) {
      return AnalyticalDataExportDialog.FIELD_EXPORT_OPTIONS.slice(1);
    }
    return AnalyticalDataExportDialog.FIELD_EXPORT_OPTIONS;
  }

  render() {
    let fileExtension: string;

    switch (this.props.exportOption) {
      case EXPORT_FILE_FORMATS.PNG_FORMAT:
        fileExtension = ".png";
        break;
      case EXPORT_FILE_FORMATS.GDA_FORMAT:
        fileExtension = ".gda";
        break;
    }

    return (
      <Dialog
        closeFn={this.props.onClose}
        mainActionButtonProps={{
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting,
          children: "Export",
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        footerMessage={{ message: this.props.error }}
      >
        <ReactSelectReduxFormField
          name={AnalyticalDataExportDialog.FIELD_EXPORT_OPTION}
          label="Format"
          placeholder="Please select..."
          options={this.getFieldExportOptions()}
          component={ReactSelectField}
          isClearable={false}
          isSearchable={false}
        />

        {this.props.exportOption !== EXPORT_FILE_FORMATS.PNG_FORMAT && (
          <span>
            <ReactSelectReduxFormField
              name={AnalyticalDataExportDialog.FIELD_EXPORT_SAMPLES}
              label="Options"
              placeholder="Please select..."
              options={AnalyticalDataExportDialog.FIELD_EXPORT_SAMPLES_OPTIONS}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={false}
            />

            <ReactSelectReduxFormField
              name={AnalyticalDataExportDialog.FIELD_EXPORT_CONDITIONS}
              options={AnalyticalDataExportDialog.FIELD_EXPORT_CONDITIONS_OPTIONS}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={false}
            />

            <ReactSelectReduxFormField
              name={AnalyticalDataExportDialog.FIELD_EXPORT_DATASERIES}
              options={AnalyticalDataExportDialog.FIELD_EXPORT_DATASERIES_OPTIONS}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={false}
            />
          </span>
        )}

        <InputReduxFormField
          componentClass="input"
          name={AnalyticalDataExportDialog.FIELD_EXPORT_FILENAME}
          label={"File name"}
          placeholder={`Please provide a name for the file to be downloaded`}
          component={InputField}
          validate={[fileNameFieldValidation, simpleFieldValidation]}
          suffix={<Addon label={fileExtension} />}
        />
      </Dialog>
    );
  }
}

export default connect<IExportDialogAppStateProps, IExportDialogDispatchProps, IExportDialogOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AnalyticalDataExportDialog.FORM_NAME,
  })(AnalyticalDataExportDialog) as any,
);

export { AnalyticalDataExportDialog };
